'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { claimLive } from '@/app/apis';
import { useToast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import {
  type LiveAvatar,
  type LiveClaimDebugMetadata,
  LiveClaimError,
  type LiveV1AvatarWithVoice,
  type UserInfo,
} from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { NEW_CHARACTER_ICON } from '../_constants/media';
import { hasLiveCallConsent, saveLiveCallConsent } from '../_hooks/live-call-consent';
import {
  confirmLiveSeniorNotice,
  deleteUserAvatar,
  getLiveComplianceStatus,
  listPresetAvatars,
  listUserAvatars,
  submitLiveEmergencyContact,
  verifyLiveIdentity,
} from '../apis';
import { createLiveDebugLogger, describeError, sanitizeString } from '../live-debug-log';
import type { LiveV1ComplianceStatusReply } from '../types';
import {
  getAvatarPreviewImage,
  getAvatarProfilePhotoImage,
  getAvatarVoiceLibrary,
  isUserBannerAvatar,
  reportStreamElementClick,
} from '../utils';
import { BannerAvatarCard } from './BannerAvatarCard';
import { BannerAvatarCardSkeleton } from './BannerAvatarCardSkeleton';
import {
  type ContactFormError,
  getComplianceStep,
  getContactFormError,
  getIdentityFormError,
  type IdentityFormError,
  LiveComplianceModal,
} from './compliance';
import { LiveCallConsentModal } from './LiveCallConsentModal';
import { ShareAvatarDialog } from './ShareAvatarDialog';

const CreateMyOwnScreen = dynamic(
  () => import('./create-my-own/CreateMyOwnScreen').then((mod) => mod.CreateMyOwnScreen),
  { ssr: false },
);

/** UGC 数量信息，仅 user 分支返回；guest / 上限未知时为 null。 */
interface BannerAvatarCounts {
  active: number;
  max: number;
}

interface BannerRolesData {
  ugcRoles: LiveAvatar[];
  presetRoles: LiveAvatar[];
  counts: BannerAvatarCounts | null;
}

// 模块级缓存：跨路由切换（如从 /vidu-stream/[id] 返回）保留数据，命中即跳过初次请求。
// 登录/登出等主动场景会走 refreshBannerRoles 覆盖，无需 TTL。
let bannerRolesCache: BannerRolesData | null = null;

// UGC 块一行 6 槽（含新建卡）。
const UGC_ROW_SLOTS = 6;
// 有 UGC 时 PGC 独立成块，展示 12 个（2 行）。
const PRESET_DISPLAY_COUNT = 12;
// 无 UGC 时合并单块：新建卡 + 11 预设 = 12（2 行）。
const PRESET_DISPLAY_COUNT_MERGED = 11;
// 首个骨架屏 + 11 个 = 12，占满 2 行（6 列）。
const BANNER_SKELETON_KEYS = Array.from({ length: 11 }, (_, i) => `banner-skeleton-${i + 1}`);

export function BannerRoleSection() {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations();
  const { toast } = useToast();
  const userInfo = useStore((s) => s.userInfo);
  const setLiveAvatar = useStore((s) => s.setLiveAvatar);
  const setLiveSession = useStore((s) => s.setLiveSession);
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const storeApi = useStoreApi();

  const [ugcRoles, setUgcRoles] = useState<LiveAvatar[]>(() => bannerRolesCache?.ugcRoles ?? []);
  const [presetRoles, setPresetRoles] = useState<LiveAvatar[]>(() => bannerRolesCache?.presetRoles ?? []);
  const [counts, setCounts] = useState<BannerAvatarCounts | null>(() => bannerRolesCache?.counts ?? null);
  const [isLoading, setIsLoading] = useState(bannerRolesCache === null);
  const hasFetchedRef = useRef(false);

  const [liveCallConsentOpen, setLiveCallConsentOpen] = useState(false);
  const pendingConsentActionRef = useRef<(() => void | Promise<void>) | null>(null);
  const pendingConsentUserIdRef = useRef<string | null>(null);
  const [liveComplianceOpen, setLiveComplianceOpen] = useState(false);
  const [liveComplianceStatus, setLiveComplianceStatus] = useState<LiveV1ComplianceStatusReply | null>(null);
  const [isLiveComplianceSubmitting, setIsLiveComplianceSubmitting] = useState(false);
  const [liveContactError, setLiveContactError] = useState<ContactFormError>();
  const [liveIdentityError, setLiveIdentityError] = useState<IdentityFormError>();
  const pendingComplianceActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const [isCreateMyOwnOpen, setIsCreateMyOwnOpen] = useState(false);
  const [createMyOwnInitialData, setCreateMyOwnInitialData] = useState<LiveV1AvatarWithVoice | undefined>();
  const [shareTarget, setShareTarget] = useState<LiveAvatar | null>(null);
  const [claimingAvatarId, setClaimingAvatarId] = useState<string | null>(null);

  const refreshBannerRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      // UGC / PGC 分开拉取，各自可独立翻页。UGC 失败（如游客无 token）静默降级为空，
      // 只要 PGC 拿到就正常渲染；PGC 失败才丢弃整批（保留上次数据）。
      const [ugcRes, presetRes] = await Promise.all([listUserAvatars(), listPresetAvatars()]);
      if (presetRes.error) {
        return;
      }

      const ugcData = ugcRes.error ? undefined : ugcRes.data;
      const active = ugcData?.active_avatar_count;
      const max = ugcData?.max_avatar_count;
      const next: BannerRolesData = {
        ugcRoles: ugcData?.roles ?? [],
        presetRoles: presetRes.data?.roles ?? [],
        // active/max 都拿到才算有效上限信息；否则视为未知（放行创建）。
        counts: typeof active === 'number' && typeof max === 'number' ? { active, max } : null,
      };

      bannerRolesCache = next;
      setUgcRoles(next.ugcRoles);
      setPresetRoles(next.presetRoles);
      setCounts(next.counts);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requireLogin = useCallback(
    (referElement: string, onLoggedIn: (user: UserInfo) => void) => {
      if (userInfo) {
        onLoggedIn(userInfo);
        return;
      }

      openLoginDialog({
        onClose: (loggedInUser) => {
          if (loggedInUser) {
            void refreshBannerRoles();
            onLoggedIn(loggedInUser);
          }
        },
      });
      reportEvent('login_pop_exposure', {
        refer_element: referElement,
        refer_page: pathname,
      });
    },
    [openLoginDialog, pathname, refreshBannerRoles, userInfo],
  );

  const runWithLiveCallConsent = useCallback(
    (action: () => void | Promise<void>, userIdOverride?: string) => {
      const userId = userIdOverride ?? storeApi.getState().userInfo?.id;
      if (!userId) {
        return;
      }

      if (hasLiveCallConsent(userId)) {
        void action();
        return;
      }

      pendingConsentUserIdRef.current = userId;
      pendingConsentActionRef.current = action;
      setLiveCallConsentOpen(true);
    },
    [storeApi],
  );

  const handleLiveCallConsentOpenChange = useCallback((open: boolean) => {
    setLiveCallConsentOpen(open);
    if (!open) {
      pendingConsentActionRef.current = null;
      pendingConsentUserIdRef.current = null;
    }
  }, []);

  const handleLiveCallConsentConfirm = useCallback(() => {
    const userId = pendingConsentUserIdRef.current ?? storeApi.getState().userInfo?.id;
    if (userId) {
      saveLiveCallConsent(userId);
    }

    pendingConsentUserIdRef.current = null;
    const action = pendingConsentActionRef.current;
    pendingConsentActionRef.current = null;
    if (action) {
      void action();
    }
  }, [storeApi]);

  const completeLiveCompliance = useCallback((status: LiveV1ComplianceStatusReply) => {
    setLiveComplianceStatus(status);
    if (getComplianceStep(status) !== 'ready') {
      setLiveComplianceOpen(true);
      return;
    }

    setLiveComplianceOpen(false);
    const action = pendingComplianceActionRef.current;
    pendingComplianceActionRef.current = null;
    if (action) void action();
  }, []);

  const runWithLiveCompliance = useCallback(
    async (action: () => void | Promise<void>) => {
      if (SITE !== 'china') {
        await action();
        return;
      }

      pendingComplianceActionRef.current = action;
      setIsLiveComplianceSubmitting(true);
      const { data, error } = await getLiveComplianceStatus();
      setIsLiveComplianceSubmitting(false);
      if (error || !data) {
        pendingComplianceActionRef.current = null;
        toast({ type: 'error', message: '合规状态获取失败，请稍后重试。' });
        return;
      }
      completeLiveCompliance(data);
    },
    [completeLiveCompliance, toast],
  );

  const handleIdentitySubmit = useCallback(
    async (body: { real_name: string; id_no: string }) => {
      setIsLiveComplianceSubmitting(true);
      setLiveIdentityError(undefined);
      const { data, error } = await verifyLiveIdentity(body);
      setIsLiveComplianceSubmitting(false);
      if (error || !data) {
        setLiveIdentityError(getIdentityFormError(error?.reason));
        return;
      }
      completeLiveCompliance(data);
    },
    [completeLiveCompliance],
  );

  const handleContactSubmit = useCallback(
    async (body: { name: string; phone: string; relationship: string }) => {
      setIsLiveComplianceSubmitting(true);
      setLiveContactError(undefined);
      const { data, error } = await submitLiveEmergencyContact(body);
      setIsLiveComplianceSubmitting(false);
      if (error || !data) {
        setLiveContactError(getContactFormError(error?.reason));
        return;
      }
      completeLiveCompliance(data);
    },
    [completeLiveCompliance],
  );

  const handleSeniorNoticeConfirm = useCallback(async () => {
    setIsLiveComplianceSubmitting(true);
    const { data, error } = await confirmLiveSeniorNotice();
    setIsLiveComplianceSubmitting(false);
    if (error || !data) {
      toast({ type: 'error', message: '确认失败，请稍后重试。' });
      return;
    }
    completeLiveCompliance(data);
  }, [completeLiveCompliance, toast]);

  const handleLiveComplianceOpenChange = useCallback((open: boolean) => {
    setLiveComplianceOpen(open);
    if (!open) {
      pendingComplianceActionRef.current = null;
      setLiveIdentityError(undefined);
      setLiveContactError(undefined);
    }
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    // 已有模块级缓存（如从直播房间返回）就直接用，不重发请求。
    if (bannerRolesCache !== null) return;
    void refreshBannerRoles();
  }, [refreshBannerRoles]);

  const handleCreateAvatarClick = () => {
    reportStreamElementClick({ element_name: 'ugc_character_create_click' });
    requireLogin('vidu-stream - create-avatar', (loggedInUser) => {
      void runWithLiveCompliance(() => {
        runWithLiveCallConsent(() => {
          setCreateMyOwnInitialData(undefined);
          setIsCreateMyOwnOpen(true);
        }, loggedInUser.id);
      });
    });
  };

  /** 点编辑：直接用 banner list 已有的 UGC 数据回填编辑界面。
   * 不能再调 keeperGetOrCreateUserAvatar：那是"获取或创建 draft"，会重置 draft 返回空数据。 */
  const handleEditOwnAvatar = (role: LiveAvatar) => {
    reportStreamElementClick({
      element_name: 'ugc_edit_click',
    });
    requireLogin('vidu-stream - edit-own-avatar', (loggedInUser) => {
      void runWithLiveCompliance(() => {
        runWithLiveCallConsent(() => {
          setCreateMyOwnInitialData({
            avatar: role,
            voice_library: getAvatarVoiceLibrary(role),
          });
          setIsCreateMyOwnOpen(true);
        }, loggedInUser.id);
      });
    });
  };

  const handleCloseCreateMyOwn = () => {
    setIsCreateMyOwnOpen(false);
    setCreateMyOwnInitialData(undefined);
  };

  const handleClaimAvatar = async (avatarId: string, role: LiveAvatar) => {
    const trimmedAvatarId = avatarId.trim();

    // claim 进行中：正在处理的卡片再次点击忽略；点其他卡片弹提示，避免多开。
    if (claimingAvatarId) {
      if (trimmedAvatarId !== claimingAvatarId) {
        toast({ type: 'info', message: t('vidu-stream.banner.claimInProgress') });
      }
      return;
    }

    if (!trimmedAvatarId) {
      toast({ type: 'error', message: t('live.claimError') });
      return;
    }

    setClaimingAvatarId(trimmedAvatarId);

    const avatarType: 'ugc' | 'pgc' = isUserBannerAvatar(role) ? 'ugc' : 'pgc';
    // 直接抓一次 perf.now 存起来，不通过 logger.since 反推（后者会四舍五入且再读一次时钟，导致 metadata 起点偏差）。
    const perfAvailable = typeof performance !== 'undefined' && typeof performance.now === 'function';
    const logger = createLiveDebugLogger({
      initialContext: { avatar_id: trimmedAvatarId, avatar_type: avatarType },
    });
    logger.log('claim', 'claim_click');

    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        logger.log('claim', 'claim_mic_permission', { granted: false, reason: 'unsupported' });
        toast({ type: 'error', message: t('tools.voiceClone.recorder.accessUnsupported') });
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        logger.log('claim', 'claim_mic_permission', { granted: true });
      } catch (micError) {
        logger.log('claim', 'claim_mic_permission', {
          granted: false,
          ...describeError(micError),
        });
        throw micError;
      }

      const claimRequestStartedAtPerfMs = perfAvailable ? performance.now() : undefined;
      logger.mark('claim_request_start', claimRequestStartedAtPerfMs);
      logger.log('claim', 'claim_request_start');
      const claim = await claimLive({
        avatar_id: trimmedAvatarId,
        call_mode: 'video',
      }).catch((claimError) => {
        logger.log('claim', 'claim_request_result', {
          success: false,
          elapsed_ms_since_claim_request_start: logger.since('claim_request_start'),
          ...describeError(claimError, { includeMessage: true }),
        });
        throw claimError;
      });

      const claimSuccessAtPerfMs = perfAvailable ? performance.now() : undefined;
      if (claimSuccessAtPerfMs !== undefined) {
        logger.mark('claim_success', claimSuccessAtPerfMs);
      }
      logger.log('claim', 'claim_request_result', {
        success: true,
        live_id: claim.live.id,
        elapsed_ms_since_claim_request_start: logger.since('claim_request_start'),
      });

      // debugMetadata 只保存真正可序列化的原始字段；performance 不可用时直接省略耗时里程碑，
      // 不写 NaN 之类占位值（JSON.stringify 会把 NaN 变 null）。
      const debugMetadata: LiveClaimDebugMetadata = {
        session_correlation_id: logger.correlationId,
        avatar_type: avatarType,
        ...(claimRequestStartedAtPerfMs !== undefined
          ? { claim_request_started_at_perf_ms: claimRequestStartedAtPerfMs }
          : {}),
        ...(claimSuccessAtPerfMs !== undefined ? { claim_success_at_perf_ms: claimSuccessAtPerfMs } : {}),
      };

      setLiveSession({
        avatarId: trimmedAvatarId,
        avatarUrl:
          (isUserBannerAvatar(role) ? getAvatarProfilePhotoImage(role) : getAvatarPreviewImage(role)) || undefined,
        claimedAt: new Date().toISOString(),
        claim,
        debugMetadata,
      });

      setLiveAvatar(role);
      logger.log('claim', 'claim_navigate', { live_id: claim.live.id });
      // 带上 avatar_id：结束页刷新 / 重进后内存 store 已空，可据此回查角色补全头像与简介。
      router.push(`/vidu-stream/${claim.live.id}?avatar_id=${encodeURIComponent(trimmedAvatarId)}`);
    } catch (error) {
      let message: string;
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          message = t('tools.voiceClone.recorder.permissionDenied');
        } else if (
          error.name === 'NotFoundError' ||
          error.name === 'OverconstrainedError' ||
          error.name === 'NotReadableError'
        ) {
          message = t('tools.voiceClone.recorder.noDevice');
        } else {
          message = error.message || t('live.claimError');
        }
      } else {
        message =
          error instanceof LiveClaimError && error.code === 'request_failed'
            ? error.message.trim() || t('live.claimError')
            : error instanceof Error
              ? error.message
              : t('live.claimError');
      }

      logger.log('claim', 'claim_failed', {
        toast_message_sanitized: sanitizeString(message),
        ...describeError(error, { includeMessage: true }),
      });

      toast({
        type: 'error',
        message,
      });
    } finally {
      setClaimingAvatarId(null);
      // claim 侧 logger 使命结束：交接靠 debugMetadata 序列化字段，此处直接 dispose 释放引用。
      logger.dispose();
    }
  };

  const handleBannerRoleClick = (avatarId: string, role: LiveAvatar) => {
    reportStreamElementClick({
      element_name: isUserBannerAvatar(role) ? 'ugc_chat_click' : 'pgc_character_template_click',
      stream_character_name: role.name,
    });
    requireLogin('vidu-stream - banner-role', (loggedInUser) => {
      void runWithLiveCompliance(() => {
        runWithLiveCallConsent(() => handleClaimAvatar(avatarId, role), loggedInUser.id);
      });
    });
  };

  const handleShareRole = (role: LiveAvatar) => {
    reportEvent('stream_element_click', {
      element_name: 'mainpage_share_element_click',
      stream_character_name: role.name ?? '',
      stream_character_id: role.id ?? '',
      stream_character_type: isUserBannerAvatar(role) ? 'ugc' : 'pgc',
    });
    setShareTarget(role);
  };

  /** 点删除：仅弹二次确认。自建角色卡只在已登录且已建角色时渲染，无需再走登录/consent。 */
  const handleDeleteOwnAvatar = (role: LiveAvatar) => {
    reportStreamElementClick({ element_name: 'ugc_delete_click' });
    const trimmedId = role.id?.trim();
    if (!trimmedId) return;

    openConfirmDialog({
      title: t('vidu-stream.banner.deleteConfirmTitle'),
      description: t('vidu-stream.banner.deleteConfirmMessage'),
      primaryButtonText: t('vidu-stream.banner.deleteConfirmConfirm'),
      secondaryButtonText: t('vidu-stream.banner.deleteConfirmCancel'),
      onPrimaryAction: async () => {
        try {
          const { error } = await deleteUserAvatar(trimmedId);
          if (error) {
            toast({ type: 'error', message: t('live.internalErrorNotice') });
            return;
          }
          await refreshBannerRoles();
        } catch {
          toast({ type: 'error', message: t('live.internalErrorNotice') });
        }
      },
    });
  };

  const cardLabels = {
    chat: t('vidu-stream.banner.chat'),
    edit: t('vidu-stream.banner.edit'),
    delete: t('vidu-stream.banner.delete'),
    share: t('vidu-stream.avatars.menu.share'),
  };

  // active ≥ max 时隐藏新建卡；counts 为 null（guest / 上限未知）时放行。
  const canCreate = counts == null || counts.active < counts.max;
  const hasUgc = ugcRoles.length > 0;

  const renderCreateCard = () => (
    <button
      id="create-avatar"
      type="button"
      onClick={handleCreateAvatarClick}
      className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 rounded-16 border border-system-white16 bg-[rgba(44,44,45,0.70)] text-system-white backdrop-blur-[40px] transition-colors hover:border-system-white48 hover:bg-[rgba(60,60,61,0.7)]"
    >
      <img src={NEW_CHARACTER_ICON} alt="" className="h-[77px] w-auto" />
      <span className="px-3 text-center font-medium text-sm">{t('vidu-stream.banner.newCharacter')}</span>
    </button>
  );

  const renderUgcCard = (role: LiveAvatar) => {
    const avatarId = role.id?.trim() ?? '';
    if (!avatarId) {
      return null;
    }
    return (
      <BannerAvatarCard
        key={avatarId}
        avatarId={avatarId}
        role={role}
        isOwn
        inlineFooter
        isClaiming={claimingAvatarId === avatarId}
        className="w-full sm:w-full"
        chatLabel={cardLabels.chat}
        editLabel={cardLabels.edit}
        shareLabel={cardLabels.share}
        deleteLabel={cardLabels.delete}
        onChat={handleBannerRoleClick}
        onEdit={handleEditOwnAvatar}
        onShare={handleShareRole}
        onDelete={handleDeleteOwnAvatar}
      />
    );
  };

  const renderPresetCard = (role: LiveAvatar) => {
    const avatarId = role.id?.trim() ?? '';
    if (!avatarId) {
      return null;
    }
    return (
      <BannerAvatarCard
        key={avatarId}
        avatarId={avatarId}
        role={role}
        isOwn={false}
        isClaiming={claimingAvatarId === avatarId}
        className="w-full sm:w-full"
        chatLabel={cardLabels.chat}
        editLabel={cardLabels.edit}
        onChat={handleBannerRoleClick}
        inlineFooter
        onShare={handleShareRole}
      />
    );
  };

  // 合并单块（无 UGC）：新建卡占 1 格；隐藏时预设补满 12。
  const mergedPresetCount = canCreate ? PRESET_DISPLAY_COUNT_MERGED : PRESET_DISPLAY_COUNT;
  // UGC 块：新建卡在最前占 1 槽，剩余槽给自建角色。
  const ugcSlots = canCreate ? UGC_ROW_SLOTS - 1 : UGC_ROW_SLOTS;

  return (
    <>
      {/* 角色区：有 UGC 时拆「我的角色」+「官方角色」两块；无 UGC 时合并为新建卡 + 预设单块 */}
      <div className="relative z-10 mx-auto mt-6 w-full max-w-[1440px] px-8 sm:px-4">
        {isLoading ? (
          <div className="py-6" id="banner-roles">
            <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
              <BannerAvatarCardSkeleton className="w-full sm:w-full" />
              {BANNER_SKELETON_KEYS.map((key) => (
                <BannerAvatarCardSkeleton key={key} className="w-full sm:w-full" />
              ))}
            </div>
          </div>
        ) : hasUgc ? (
          <>
            {/* UGC 块：新建卡（未达上限）在最前，后接自建角色 */}
            <div className="font-normal text-white text-xl">{t('vidu-stream.banner.myCharacter')}</div>
            <div className="py-6 pb-12">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {canCreate ? renderCreateCard() : null}
                {ugcRoles.slice(0, ugcSlots).map(renderUgcCard)}
              </div>
            </div>
            {/* PGC 块：官方预设 12 个 */}
            <div className="font-normal text-white text-xl">{t('vidu-stream.banner.title')}</div>
            <div className="py-6" id="banner-roles">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {presetRoles.slice(0, PRESET_DISPLAY_COUNT).map(renderPresetCard)}
              </div>
            </div>
          </>
        ) : (
          <>
            {canCreate ? (
              <div className="text-center font-normal text-sm text-white/40">
                {t('vidu-stream.banner.selectOrCreate')}
              </div>
            ) : (
              <div className="font-normal text-white text-xl">{t('vidu-stream.banner.title')}</div>
            )}
            <div className="py-6" id="banner-roles">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {canCreate ? renderCreateCard() : null}
                {presetRoles.slice(0, mergedPresetCount).map(renderPresetCard)}
              </div>
            </div>
          </>
        )}
      </div>

      {isCreateMyOwnOpen ? (
        <CreateMyOwnScreen
          initialAvatarData={createMyOwnInitialData}
          onBack={handleCloseCreateMyOwn}
          onConfirmSuccess={() => {
            void refreshBannerRoles();
          }}
        />
      ) : null}

      <LiveCallConsentModal
        open={liveCallConsentOpen}
        onOpenChange={handleLiveCallConsentOpenChange}
        onConfirm={handleLiveCallConsentConfirm}
      />
      {SITE === 'china' && (
        <LiveComplianceModal
          open={liveComplianceOpen}
          onOpenChange={handleLiveComplianceOpenChange}
          status={liveComplianceStatus}
          submitting={isLiveComplianceSubmitting}
          contactError={liveContactError}
          identityError={liveIdentityError}
          onContactErrorChange={setLiveContactError}
          onIdentityErrorChange={setLiveIdentityError}
          onIdentitySubmit={handleIdentitySubmit}
          onContactSubmit={handleContactSubmit}
          onSeniorNoticeConfirm={handleSeniorNoticeConfirm}
        />
      )}

      {shareTarget && (
        <ShareAvatarDialog
          role={shareTarget}
          open={shareTarget !== null}
          onOpenChange={(open) => !open && setShareTarget(null)}
          shareUrl={
            shareTarget?.id?.trim() ? `${window.location.origin}/vidu-stream/share-avatar/${shareTarget.id.trim()}` : ''
          }
        />
      )}
    </>
  );
}
